/**
 * Name somebody as the owner of a hierarchy node (f4.2, generalised by f5.1).
 *
 * f4.2 shipped this against Business Unit Groups and built the seam that made
 * it reusable: the server decides which roles a level supports, so the dialog
 * only has to be told WHICH level it is looking at. f5.1 moved it out of
 * `routes/business-unit-groups/` and gave it `nodeType`, `nodeId`, `nodeName`
 * and `roleCodes` props -- the same component, parameterised, not a copy. It
 * offers `DEM` and `CEM` on a group and `CM` on a Business Unit, and f6.1 and
 * f7.1 pass their own codes without touching this file.
 *
 * Built on the prototype's `modal-scrim` / `modal` / `modal-head` /
 * `modal-body` / `modal-foot` shape through f2.2's `Modal`.
 *
 * **One account per human, and the picker lists accounts.** ADR-19's separate
 * `person` roster is SUPERSEDED: every delivery-role holder is a real Atlas
 * user account, so the candidates come from `GET /assignable-users`. Every
 * account that is not deactivated is offered -- including an `invited` one
 * nobody has signed in to yet and an `awaiting_role` one holding no platform
 * role, because owning a node is not a permission grant and pre-provisioning
 * somebody's account is exactly how their owner record exists before their
 * first sign-in.
 *
 * **The picker cannot create.** Typing a free-text address here would mint a
 * claimable sign-in credential: an `app_user` row with `entra_oid IS NULL` is
 * claimed by whoever next signs in with that address, so creating one is an
 * identity decision and not an ownership one. It stays on the Admin-only Users
 * screen (f3.2), which this dialog links to instead.
 *
 * **Replacing an owner is end then add**, two requests. When the server
 * refuses with `OWNER_ALREADY_ASSIGNED` the dialog names the holder and offers
 * to replace them; both writes are audited and both are preserved.
 */

import { useMemo, useState } from 'react';
import { Check, Search, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { AssignableUser, LookupValue } from '@/api/client';
import { useAssignOwner, useAssignableUsers, useEndAssignment } from '@/api/queries';
import { NAV_ENTRIES } from '@/navigation/navigation';
import { DialogField, Modal } from '@/routes/reference-data/Modal';
import { useSessionScope } from '@/session/SessionScopeContext';
import {
  generalMessage,
  initialsOf,
  problemFor,
} from '@/routes/business-unit-groups/groupView';
import {
  currentHolderFrom,
  matchesHolder,
} from '@/routes/business-unit-groups/ownershipView';

/**
 * Where an account is created, read from the navigation model rather than
 * typed -- `App.tsx` builds every route from the same entries, so a screen
 * that moved would move here too instead of leaving a dead link behind.
 */
const USERS_KEY = 'users';
const USERS_PATH = NAV_ENTRIES.find((entry) => entry.key === USERS_KEY)?.path;

/**
 * The pill a candidate who is not simply `active` wears.
 *
 * Labels only, and shorter than the Users screen's. The classes are the
 * existing `.pill-*` tokens -- no colour is decided in this file.
 *
 * `active` is deliberately absent: a pill on every row is noise, and the
 * absence of one already means "ordinary account". `deactivated` has no branch
 * either: the status field is f3.1's shared `UserStatus` enum, so the TYPE
 * admits it, but `GET /assignable-users` cannot RETURN it.
 */
const CANDIDATE_PILLS: Readonly<Record<string, { label: string; className: string }>> = {
  invited: { label: 'Invited', className: 'pill-pending' },
  awaiting_role: { label: 'No role', className: 'pill-warning' },
};

function pillFor(status: string) {
  if (status === 'active') return null;
  return (
    CANDIDATE_PILLS[status] ?? {
      label: status.replace(/_/g, ' '),
      className: 'pill-neutral',
    }
  );
}

export function AssignOwnerDialog({
  nodeType,
  nodeId,
  nodeName,
  roleCodes,
  deliveryRoles,
  onClose,
}: {
  /** `BUSINESS_UNIT_GROUP`, `BUSINESS_UNIT`, and the levels below as they arrive. */
  nodeType: string;
  nodeId: string;
  nodeName: string;
  /**
   * Which delivery roles this level supports. The SERVER is the authority --
   * it refuses anything else with `409 DELIVERY_ROLE_LEVEL_MISMATCH` -- so
   * this narrows the control rather than deciding anything.
   */
  roleCodes: readonly string[];
  /** The `DELIVERY_ROLE` lookup values, straight from f2.2's catalogue. */
  deliveryRoles: readonly LookupValue[];
  onClose: () => void;
}) {
  const candidates = useAssignableUsers();
  const assign = useAssignOwner();
  const end = useEndAssignment();
  // Presentation only, like every other `canSee` reader: the Users screen
  // authorizes on its own authority, so hiding the link grants nothing and
  // showing it grants nothing either.
  const mayOpenUsers = useSessionScope().canSee(USERS_KEY);

  const roles = useMemo(
    () =>
      deliveryRoles
        .filter((role) => role.is_active && roleCodes.includes(role.code))
        .sort((left, right) => left.sort_order - right.sort_order),
    [deliveryRoles, roleCodes],
  );

  // A level supporting exactly one role preselects it: choosing from a list of
  // one is a step that teaches the operator nothing. The control still renders,
  // so the role being recorded is never implicit.
  const [roleId, setRoleId] = useState(() => (roles.length === 1 ? roles[0]!.id : ''));
  const [userId, setUserId] = useState('');
  const [search, setSearch] = useState('');

  const users = candidates.data?.users ?? [];
  const matches = useMemo(
    () => users.filter((found) => matchesHolder(found.display_name, found.email, search)),
    [users, search],
  );

  const failure = assign.error ?? end.error;
  const holder = currentHolderFrom(assign.error);
  const busy = assign.isPending || end.isPending;

  function chosen(user: AssignableUser) {
    setUserId(user.id);
    setSearch('');
  }

  function submitAssignment(chosenUserId: string) {
    assign.mutate(
      {
        user_id: chosenUserId,
        delivery_role_id: roleId,
        node_type: nodeType,
        node_id: nodeId,
      },
      { onSuccess: onClose },
    );
  }

  function submit() {
    if (!userId) return;
    submitAssignment(userId);
  }

  function replaceHolder() {
    if (!holder) return;
    end.mutate(
      { assignmentId: holder.assignment_id, nodeType, nodeId },
      { onSuccess: () => submitAssignment(userId) },
    );
  }

  const selected = users.find((found) => found.id === userId);

  return (
    <Modal
      title="Assign an owner"
      subtitle={nodeName}
      onClose={onClose}
      onSubmit={holder ? replaceHolder : submit}
      busy={busy}
      submitLabel={holder ? 'Replace owner' : 'Assign'}
      submitIcon={holder ? <Check aria-hidden="true" /> : <UserPlus aria-hidden="true" />}
      layout="plain"
      message={generalMessage(
        holder ? null : failure,
        'display_name',
        'user_id',
        'node_id',
      )}
    >
      {holder && (
        <div className="alert alert-warning owner-alert" role="alert">
          <div>
            <div className="a-title">
              {holder.display_name} already holds {holder.delivery_role_code} here
            </div>
            <div className="a-body">
              Replacing them ends that assignment and adds the new one. Nothing is
              deleted: who held the role before stays on the record and in the audit
              log.
            </div>
          </div>
        </div>
      )}

      <fieldset className="mb-4">
        <div className="fieldset-title">Role</div>
        <div className="grid grid-2">
          <DialogField
            label="Delivery role"
            htmlFor="owner-role"
            problem={problemFor(failure, 'delivery_role_id')}
            hint="Ownership decides access"
          >
            <select
              id="owner-role"
              value={roleId}
              required
              disabled={Boolean(holder)}
              onChange={(event) => setRoleId(event.target.value)}
            >
              <option value="">Choose a role</option>
              {/* Labels come from the lookup; there is no map from code to
                  label anywhere in this application. */}
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </DialogField>
        </div>
      </fieldset>

      <fieldset>
        <div className="fieldset-title">Owner</div>
        <div className="search-field owner-search">
          <Search aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder="Search by name or e-mail"
            aria-label="Search by name or e-mail"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {candidates.isPending ? (
          <span className="skeleton-bar group-skeleton-line" aria-busy="true" />
        ) : (
          <ul className="owner-picker">
            {matches.map((found) => {
              const pill = pillFor(found.status);
              return (
                <li key={found.id}>
                  <button
                    type="button"
                    className={`owner-choice${found.id === userId ? ' is-chosen' : ''}`}
                    aria-pressed={found.id === userId}
                    onClick={() => chosen(found)}
                  >
                    <span className="avatar sm" aria-hidden="true">
                      {initialsOf(found.display_name)}
                    </span>
                    {/* The address, not just the name: two accounts may be
                        called the same thing, and the e-mail is what tells
                        the operator which human they are naming. */}
                    <span className="user-meta">
                      <span className="user-name">{found.display_name}</span>
                      <span className="owner-mail">{found.email}</span>
                    </span>
                    {pill && (
                      <span className={`badge owner-status ${pill.className}`}>
                        {pill.label}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
            {matches.length === 0 && (
              <li className="hint">
                {users.length === 0
                  ? 'No account can be named as an owner yet. Every candidate here is an Atlas account, so somebody has to be added on the Users screen first.'
                  : 'No account matches that name or e-mail.'}
              </li>
            )}
          </ul>
        )}

        {/* No create affordance. An `app_user` row with no Entra object id is
            a claimable sign-in credential -- the first sign-in with that
            address takes it -- so minting one is an identity decision that
            stays on the Admin-only Users screen, never a side effect of
            naming an owner. */}
        <p className="hint owner-note">
          Somebody missing? Accounts are added on the{' '}
          {/* Linked only for a caller who may actually open that screen. */}
          {mayOpenUsers && USERS_PATH ? (
            <Link className="cell-link" to={USERS_PATH}>
              Users
            </Link>
          ) : (
            'Users'
          )}{' '}
          screen, including for people who have never signed in. They can be named
          here as soon as the account exists.
        </p>

        {selected && (
          <span className="hint">
            {selected.display_name} ({selected.email}) will be recorded as the owner.
          </span>
        )}
      </fieldset>
    </Modal>
  );
}
